import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEventHandler,
  type Ref,
} from "react";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { formComboboxInputClassName } from "@/components/forms/FormCombobox";
import { cn } from "cn";

interface OptionType {
  inputValue?: string;
  label: string;
  id?: string;
}

interface AutocompleteProps {
  options: OptionType[];
  id?: string;
  name?: string;
  placeholder?: string;
  addNewLabel?: string;
  onChange: (newVal: OptionType | null) => unknown;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  inputRef?: Ref<HTMLInputElement>;
  "aria-describedby"?: string;
  value: OptionType | null;
  isErrored: boolean;
}

/**
 * Campaign picker with optional "Add New Campaign" creatable row.
 *
 * Close policy (same as StringFormCombobox / old MUI `clearOnBlur`): blur,
 * outside click, or Escape reverts the input to the last committed campaign.
 * New names are committed only by selecting the creatable row (or Enter while
 * it is highlighted via `autoHighlight`) — not by leaving leftover filter text.
 */
function CampaignSelect(props: AutocompleteProps) {
  const committedLabel = props.value?.label ?? "";
  const committedLabelRef = useRef(committedLabel);
  committedLabelRef.current = committedLabel;

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(committedLabel);

  useEffect(() => {
    if (!open) setInputValue(committedLabel);
  }, [committedLabel, open]);

  const items = useMemo(() => {
    const list = [...(props.options || [])];
    const current = props.value;
    // Keep free-solo / new campaigns (`{ label }` without id) in `items` so the
    // controlled value remains selectable and displays correctly.
    if (
      current?.label &&
      !list.some(
        (option) =>
          (current.id && option.id && option.id === current.id) ||
          option.label === current.label
      )
    ) {
      list.push(current);
    }
    const trimmed = inputValue.trim();
    if (trimmed) {
      const isExisting = list.some((option) => trimmed === option.label);
      if (!isExisting) {
        list.push({
          inputValue: trimmed,
          label: `Add New Campaign: "${trimmed}"`,
        });
      }
    }
    return list;
  }, [props.options, props.value, inputValue]);

  const applySelection = (next: OptionType | null) => {
    if (next == null) {
      committedLabelRef.current = "";
      setInputValue("");
      props.onChange(null);
      return;
    }
    const committed = next.inputValue
      ? { label: next.inputValue }
      : next;
    committedLabelRef.current = committed.label;
    setInputValue(committed.label);
    props.onChange(committed);
  };

  return (
    <Combobox
      items={items}
      value={props.value}
      inputValue={inputValue}
      onValueChange={applySelection}
      onInputValueChange={setInputValue}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) return;
        // clearOnBlur: discard filter text; do not commit it as a new campaign.
        setInputValue(committedLabelRef.current);
      }}
      autoHighlight
      autoComplete="off"
      itemToStringLabel={(option) => {
        if (option.inputValue) return option.inputValue;
        return option.label;
      }}
      isItemEqualToValue={(a, b) =>
        (a.id && b.id && a.id === b.id) || a.label === b.label
      }
    >
      <ComboboxInput
        id={props.id}
        name={props.name}
        ref={props.inputRef}
        onBlur={props.onBlur}
        aria-describedby={props["aria-describedby"]}
        placeholder={props.placeholder}
        showClear
        aria-invalid={props.isErrored || undefined}
        className={cn(
          formComboboxInputClassName,
          props.isErrored && "border-destructive"
        )}
      />
      <ComboboxContent>
        <ComboboxEmpty>No campaigns found.</ComboboxEmpty>
        <ComboboxList>
          {(option) => (
            <ComboboxItem key={option.id ?? option.label} value={option}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

export default CampaignSelect;
